import { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { supabase } from '../../supabase'
import { STOCK_STATUSES, STOCK_META, stockStatusOf } from '../../utils/stockStatus'
import {
  t, Stagger, Panel, PanelHead, PanelTitle, PageHead, PageTitle, PageSub,
  Btn, Input, Textarea, Label, Field, Row, Cluster, Muted,
  ListItem, ItemTitle, ItemMeta, Loading, Empty, Spinner, useUI,
} from './ui'

const Split = styled.div`
  display: grid;
  grid-template-columns: ${(p) => (p.$open ? '330px 1fr' : '1fr')};
  gap: 20px;
  align-items: start;
  @media (max-width: 1000px) { grid-template-columns: 1fr; }
`
const Col = styled.div`display: flex; flex-direction: column; gap: 16px;`

const StockDot = styled.span`
  width: 9px; height: 9px; border-radius: 50%;
  background: ${(p) => p.$c}; flex-shrink: 0;
`
const Radio = styled.label`
  display: flex; align-items: center; gap: 8px;
  padding: 8px 11px; border-radius: ${t.rSm};
  cursor: pointer;
  font-family: ${t.font}; font-size: 0.82rem; font-weight: 600;
  color: ${(p) => (p.$on ? t.text : t.textSoft)};
  background: ${(p) => (p.$on ? t.goldWash : t.surface)};
  border: 1.5px solid ${(p) => (p.$on ? t.gold : t.line)};
  transition: all 0.16s;
  &:hover { border-color: ${(p) => (p.$on ? t.gold : t.textFaint)}; }
  input { accent-color: ${t.gold}; }
`
const Toggle = styled.button`
  display: flex; align-items: center; gap: 8px;
  padding: 9px 13px; border-radius: ${t.rSm}; cursor: pointer;
  font-family: ${t.font}; font-size: 0.82rem; font-weight: 700;
  background: ${(p) => (p.$on ? t.goldWash : t.surfaceSunk)};
  border: 1.5px solid ${(p) => (p.$on ? t.gold : t.line)};
  color: ${(p) => (p.$on ? t.goldDeep : t.textSoft)};
  transition: all 0.18s;
`
const OrderRow = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 9px 12px; margin-bottom: 5px; border-radius: ${t.rSm};
  font-family: ${t.font}; font-size: 0.8rem; color: ${t.text};
  background: ${(p) => (p.$over ? t.goldWash : t.surface)};
  border: 1.5px solid ${(p) => (p.$over ? t.gold : t.line)};
  cursor: grab;
  opacity: ${(p) => (p.$drag ? 0.4 : 1)};
  transition: all 0.15s;
`
const Grip = styled.span`color: ${t.textFaint}; font-size: 0.9rem;`
const Rank = styled.span`
  width: 20px; height: 20px; border-radius: 50%;
  background: ${t.ink}; color: ${t.goldLight};
  font-size: 0.65rem; font-weight: 800;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
`
const PhotoGrid = styled.div`display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 16px;`
const Photo = styled.div`
  position: relative; width: 112px; height: 112px;
  cursor: grab; border-radius: 11px;
  opacity: ${(p) => (p.$drag ? 0.4 : 1)};
  outline: ${(p) => (p.$over ? `3px solid ${t.gold}` : 'none')};
  outline-offset: 2px;
  transition: opacity 0.15s, outline 0.15s;
  img { width: 100%; height: 100%; object-fit: cover; border-radius: 10px; border: 1px solid ${t.line}; }
`
const PhotoTag = styled.span`
  position: absolute; ${(p) => (p.$bottom ? 'bottom: 6px;' : 'top: 6px;')} left: 6px;
  font-family: ${t.font}; font-size: 0.575rem; font-weight: 620;
  padding: 2px 6px; border-radius: ${t.rXs};
  background: ${(p) => (p.$gold ? t.gold : 'rgba(22,24,28,0.7)')};
  color: #fff;
`
const PhotoDel = styled.button`
  position: absolute; top: -6px; right: -6px;
  width: 20px; height: 20px; border-radius: 50%;
  background: ${t.red}; color: #fff; border: 2px solid ${t.surface};
  cursor: pointer; font-size: 10px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
`

export default function ProduitsAdmin() {
  const { toast, confirm } = useUI()
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [form, setForm] = useState({})
  const [images, setImages] = useState([])
  const [uploading, setUploading] = useState(false)
  const [dragging, setDragging] = useState(null)
  const [dragOver, setDragOver] = useState(null)
  const [draggingHome, setDraggingHome] = useState(null)
  const fileRef = useRef()

  useEffect(() => { fetchProducts() }, [])

  const fetchProducts = async () => {
    setLoading(true)
    const { data } = await supabase.from('products').select('*, product_images(*)').order('home_order').order('created_at')
    setProducts(data || [])
    setLoading(false)
  }

  const homeCount = products.filter((p) => p.show_on_home !== false).length

  const reorderHome = async (fromIdx, toIdx) => {
    const homeProducts = products.filter((p) => p.show_on_home !== false)
    const others = products.filter((p) => p.show_on_home === false)
    const reordered = [...homeProducts]
    const [moved] = reordered.splice(fromIdx, 1)
    reordered.splice(toIdx, 0, moved)
    await Promise.all(reordered.map((p, i) => supabase.from('products').update({ home_order: i }).eq('id', p.id)))
    setProducts([...reordered, ...others])
    toast('Ordre mis à jour')
  }

  const selectProduct = (p) => {
    setSelected(p)
    setForm({
      name: p.name,
      description: p.description || '',
      price: p.price || '',
      unit: p.unit || '',
      stock_status: stockStatusOf(p),
      format: p.format || '',
      dimensions: p.dimensions || '',
      poids: p.poids || '',
      coupes: (p.coupes || []).join(', '),
      usages: (p.usages || []).join(', '),
      show_on_home: p.show_on_home !== false,
    })
    setImages([...(p.product_images || [])].sort((a, b) => (a.order_index || 0) - (b.order_index || 0)))
  }

  const newProduct = () => {
    setSelected('new')
    setForm({ name: '', description: '', price: '', unit: '', stock_status: 'available', format: '', dimensions: '', poids: '', coupes: '', usages: '', show_on_home: true })
    setImages([])
  }

  const save = async () => {
    if (!form.name.trim()) return toast('Le nom est requis', 'error')
    if (form.show_on_home) {
      const others = products.filter((p) => p.show_on_home !== false && p.id !== selected?.id).length
      if (others >= 4) return toast('Maximum 4 produits affichés en accueil', 'error')
    }
    const payload = {
      name: form.name, description: form.description,
      price: form.price ? parseFloat(form.price) : null,
      unit: form.unit, format: form.format,
      stock_status: form.stock_status,
      // `available` reste synchronisé au sens « commandable » (dispo ou liste d'attente)
      available: form.stock_status !== 'unavailable',
      dimensions: form.dimensions, poids: form.poids,
      coupes: form.coupes ? form.coupes.split(',').map((s) => s.trim()).filter(Boolean) : [],
      usages: form.usages ? form.usages.split(',').map((s) => s.trim()).filter(Boolean) : [],
      show_on_home: form.show_on_home,
      home_order: form.show_on_home ? (selected === 'new' ? homeCount : selected.home_order || 0) : null,
    }
    if (selected === 'new') {
      const { data, error } = await supabase.from('products').insert([payload]).select().single()
      if (error) return toast('Erreur lors de la création', 'error')
      toast('Produit créé')
      setSelected(data)
    } else {
      const { error } = await supabase.from('products').update(payload).eq('id', selected.id)
      if (error) return toast('Erreur lors de la mise à jour', 'error')
      toast('Produit mis à jour')
      setSelected((prev) => ({ ...prev, ...payload }))
    }
    fetchProducts()
  }

  const deleteProduct = async (p) => {
    const ok = await confirm({
      icon: '🗑️', tone: 'red',
      title: 'Supprimer ce produit ?',
      body: <><strong>{p.name}</strong> et ses photos seront retirés du site. Cette action est irréversible.</>,
      confirmLabel: 'Supprimer',
    })
    if (!ok) return
    await supabase.from('products').delete().eq('id', p.id)
    setSelected(null)
    toast('Produit supprimé')
    fetchProducts()
  }

  const uploadImage = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    setUploading(true)
    const ext = file.name.split('.').pop()
    const path = `${selected.id || 'new'}/${Date.now()}.${ext}`
    const { error: upErr } = await supabase.storage.from('product-images').upload(path, file)
    if (upErr) { toast('Erreur upload', 'error'); setUploading(false); return }
    const { data: { publicUrl } } = supabase.storage.from('product-images').getPublicUrl(path)
    const productId = selected === 'new' ? null : selected.id
    if (productId) {
      const { data: newImg } = await supabase.from('product_images').insert([{
        product_id: productId, url: publicUrl, order_index: images.length,
      }]).select().single()
      setImages((prev) => [...prev, newImg])
      fetchProducts()
    }
    setUploading(false)
    toast('Image ajoutée')
    e.target.value = ''
  }

  const deleteImage = async (img) => {
    await supabase.from('product_images').delete().eq('id', img.id)
    const newImages = images.filter((i) => i.id !== img.id)
    setImages(newImages)
    await Promise.all(newImages.map((im, idx) => supabase.from('product_images').update({ order_index: idx }).eq('id', im.id)))
    fetchProducts()
    toast('Image supprimée')
  }

  const onDrop = async (e, idx) => {
    e.preventDefault()
    if (dragging === null || dragging === idx) { setDragging(null); setDragOver(null); return }
    const newImages = [...images]
    const [moved] = newImages.splice(dragging, 1)
    newImages.splice(idx, 0, moved)
    setImages(newImages)
    setDragging(null); setDragOver(null)
    await Promise.all(newImages.map((img, i) => supabase.from('product_images').update({ order_index: i }).eq('id', img.id)))
    toast('Ordre des photos mis à jour')
    fetchProducts()
  }

  const homeProducts = products.filter((p) => p.show_on_home !== false)

  return (
    <>
      <PageHead>
        <div>
          <PageTitle>Produits</PageTitle>
          <PageSub>{products.length} produit(s) · {homeCount}/4 affichés en page d'accueil</PageSub>
        </div>
        <Btn $variant="gold" onClick={newProduct}>+ Nouveau produit</Btn>
      </PageHead>

      <Split $open={!!selected}>
        {/* ── Liste ── */}
        <Col>
          {homeProducts.length > 1 && (
            <Panel $pad="16px">
              <Label>Ordre en page d'accueil</Label>
              {homeProducts.map((p, i) => (
                <OrderRow
                  key={p.id}
                  draggable
                  $over={dragOver === i && draggingHome !== null}
                  $drag={draggingHome === i}
                  onDragStart={() => setDraggingHome(i)}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(i) }}
                  onDrop={() => { reorderHome(draggingHome, i); setDraggingHome(null); setDragOver(null) }}
                  onDragEnd={() => { setDraggingHome(null); setDragOver(null) }}
                >
                  <Grip>⠿</Grip>
                  <Rank>{i + 1}</Rank>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                </OrderRow>
              ))}
            </Panel>
          )}

          <div>
            {loading ? (
              <Loading rows={4} h="62px" />
            ) : products.length === 0 ? (
              <Empty icon="🛍️" title="Aucun produit">Créez votre premier produit pour l'afficher sur le site.</Empty>
            ) : (
              products.map((p, i) => {
                const meta = STOCK_META[stockStatusOf(p)]
                return (
                  <Stagger key={p.id} $i={i}>
                    <ListItem $on={selected?.id === p.id} onClick={() => selectProduct(p)}>
                      <Cluster $gap="10px" $align="center">
                        <StockDot $c={meta.dot} title={meta.label} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <ItemTitle>{p.name}</ItemTitle>
                          <ItemMeta>
                            {p.product_images?.length || 0} photo(s)
                            {p.show_on_home !== false && ' · 🏠 accueil'}
                          </ItemMeta>
                        </div>
                      </Cluster>
                    </ListItem>
                  </Stagger>
                )
              })
            )}
          </div>
        </Col>

        {/* ── Formulaire ── */}
        {selected && (
          <Col>
            <Stagger>
              <Panel>
                <PanelHead>
                  <PanelTitle>{selected === 'new' ? 'Nouveau produit' : form.name}</PanelTitle>
                  <Btn $variant="quiet" $size="sm" onClick={() => setSelected(null)}>✕ Fermer</Btn>
                </PanelHead>

                <Row $cols="1fr 1fr">
                  <Field><Label>Nom du produit *</Label><Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="ex : Paille de blé" /></Field>
                  <Field><Label>Format</Label><Input value={form.format} onChange={(e) => setForm((p) => ({ ...p, format: e.target.value }))} placeholder="ex : Bottes carrées" /></Field>
                </Row>
                <Row $cols="1fr 1fr">
                  <Field><Label>Dimensions (cm)</Label><Input value={form.dimensions} onChange={(e) => setForm((p) => ({ ...p, dimensions: e.target.value }))} placeholder="ex : 90 x 120 x 240" /></Field>
                  <Field><Label>Poids moyen</Label><Input value={form.poids} onChange={(e) => setForm((p) => ({ ...p, poids: e.target.value }))} placeholder="ex : 440 kg" /></Field>
                </Row>
                <Row $cols="1fr 1fr">
                  <Field><Label>Coupes (séparées par virgule)</Label><Input value={form.coupes} onChange={(e) => setForm((p) => ({ ...p, coupes: e.target.value }))} placeholder="Hachée, Courte, Longue" /></Field>
                  <Field><Label>Usages (séparés par virgule)</Label><Input value={form.usages} onChange={(e) => setForm((p) => ({ ...p, usages: e.target.value }))} placeholder="Chevaux, Bovins" /></Field>
                </Row>
                <Field>
                  <Label>Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} placeholder="Description du produit…" />
                </Field>

                <Row $cols="180px 1fr auto">
                  <Field>
                    <Label>Prix indicatif (€)</Label>
                    <Input type="number" value={form.price} onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))} placeholder="120" />
                  </Field>
                  <Field>
                    <Label>Disponibilité</Label>
                    <Cluster $gap="7px">
                      {STOCK_STATUSES.map((s) => (
                        <Radio key={s} $on={form.stock_status === s}>
                          <input type="radio" checked={form.stock_status === s} onChange={() => setForm((p) => ({ ...p, stock_status: s }))} />
                          {STOCK_META[s].adminLabel}
                        </Radio>
                      ))}
                    </Cluster>
                  </Field>
                  <Field>
                    <Label>Accueil</Label>
                    <Toggle
                      $on={form.show_on_home}
                      onClick={() => {
                        if (!form.show_on_home && homeCount >= 4 && selected !== 'new') return toast('Maximum 4 produits en accueil', 'error')
                        setForm((p) => ({ ...p, show_on_home: !p.show_on_home }))
                      }}
                    >
                      🏠 {form.show_on_home ? 'Affiché' : 'Masqué'}
                    </Toggle>
                  </Field>
                </Row>

                <Cluster $end $gap="10px">
                  {selected !== 'new' && <Btn $variant="danger" onClick={() => deleteProduct(selected)}>Supprimer</Btn>}
                  <Btn $variant="gold" onClick={save}>Enregistrer</Btn>
                </Cluster>
              </Panel>
            </Stagger>

            {/* ── Photos ── */}
            {selected !== 'new' && (
              <Stagger $i={1}>
                <Panel>
                  <PanelHead>
                    <PanelTitle $sm>Photos</PanelTitle>
                    <Muted style={{ fontSize: '0.74rem' }}>Glisser-déposer pour réordonner · la 1ʳᵉ s'affiche en accueil</Muted>
                  </PanelHead>

                  {images.length === 0 ? (
                    <Empty icon="📷" title="Aucune photo">Ajoutez au moins une image pour la fiche produit.</Empty>
                  ) : (
                    <PhotoGrid>
                      {images.map((img, idx) => (
                        <Photo
                          key={img.id}
                          draggable
                          $drag={dragging === idx}
                          $over={dragOver === idx && dragging !== null}
                          onDragStart={(e) => { setDragging(idx); e.dataTransfer.effectAllowed = 'move' }}
                          onDragOver={(e) => { e.preventDefault(); setDragOver(idx) }}
                          onDrop={(e) => onDrop(e, idx)}
                          onDragEnd={() => { setDragging(null); setDragOver(null) }}
                        >
                          <img src={img.url} alt="" />
                          <PhotoTag>#{idx + 1}</PhotoTag>
                          {idx === 0 && <PhotoTag $bottom $gold>🏠 Accueil</PhotoTag>}
                          <PhotoDel onClick={() => deleteImage(img)} title="Supprimer la photo">✕</PhotoDel>
                        </Photo>
                      ))}
                    </PhotoGrid>
                  )}

                  <input ref={fileRef} type="file" accept="image/*" onChange={uploadImage} style={{ display: 'none' }} />
                  <Btn $variant="ghost" onClick={() => fileRef.current.click()} disabled={uploading}>
                    {uploading ? <><Spinner /> Envoi…</> : '📸 Ajouter une photo'}
                  </Btn>
                </Panel>
              </Stagger>
            )}
          </Col>
        )}
      </Split>
    </>
  )
}
